import styles from './Users.module.css';

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Highlighter from 'react-highlight-words';
import { Link } from 'react-router-dom';
import { withRouter } from 'react-router';
import { PageHeader, Card, Spin, Button, Tooltip, Table, Input, Typography, Modal, Divider, Checkbox } from 'antd';

import { ReloadOutlined, SearchOutlined, ProfileOutlined } from '@ant-design/icons';

import { getUsers, clearUsers, getMails } from 'store/ducks/users.js';

import formatDistance from 'date-fns/formatDistance';
import _get from 'lodash/get';
import _uniq from 'lodash/uniq';
import _sumBy from 'lodash/sumBy';

const safeFormatDistance = (from, to) => {
  try {
    from = new Date(from);
    to = typeof to === 'undefined' ? new Date() : new Date(to);

    return formatDistance(from, to, { addSuffix: true });
  } catch (e) {
    console.error(e);

    return 'INVALID TIME';
  }
};

const BILLING_PLANS = [
  { name: 'Trial', value: 'trial', color: '#454442' },
  { name: 'Pro', value: 'pro', color: '#FF623D' },
  { name: 'Personal', value: 'personal', color: '#9061F9' },
  { name: 'Starter', value: 'starter', color: '#2675D1' },
  { name: 'NULL', value: null, color: '#DDDBD7' }
];

const BILLING_PLANS_STATUSES = [
  { name: 'Active', value: 'active', color: '#0F9F6E' },
  { name: 'Unpaid', value: 'unpaid', color: '#F05252' },
  { name: 'Canceled', value: 'canceled', color: '#DDDBD7' },
  { name: 'NULL', value: null, color: '#DDDBD7' }
];

const dimmedNullJsx = <span style={{ color: '#DDDBD7' }}>NULL</span>;

const round = (v) => Math.round(v * 100) / 100;

// Sum of completion rates of all started tutorials
// (how much time user invested in tutorials)
const getPlayedTutorialsSortNum = (playedTutorials) => {
  playedTutorials = _uniq(playedTutorials || []);

  const playedTutorialsCount = playedTutorials.length;

  if (!playedTutorialsCount) {
    return 0;
  }

  return _sumBy(playedTutorials, 'completionRatio') || 0;
};

const Users = ({ history }) => {
  const dispatch = useDispatch();

  const users = useSelector((state) => state.users.users);
  const mails = useSelector((state) => state.users.mails);
  const getUsersLoading = useSelector((state) => state.users.getUsers.loading);
  const getMailsLoading = useSelector((state) => state.users.getMails.loading);

  const [showEmails, setShowEmails] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');

  const reload = () => dispatch(getUsers());

  const searchInput = React.createRef();

  useEffect(() => {
    dispatch(getUsers());

    return () => {
      dispatch(clearUsers());
    };
  }, []);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();

    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };

  const openMailsModal = () => {
    dispatch(getMails());
    setShowEmails(true);
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90, marginRight: 8 }}>
          Search
        </Button>
        <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
          Reset
        </Button>
      </div>
    ),
    filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilter: (value, record) => (record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : null),
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) setTimeout(() => searchInput.current.select());
    },
    render: (text) =>
      text ? (
        searchedColumn === dataIndex ? (
          <Typography.Paragraph style={{ margin: 0 }} copyable={{ text }}>
            <Highlighter
              highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
              searchWords={[searchText]}
              autoEscape
              textToHighlight={text.toString()}
            />
          </Typography.Paragraph>
        ) : (
          <Typography.Paragraph style={{ margin: 0 }} copyable>
            {text}
          </Typography.Paragraph>
        )
      ) : null
  });

  const pagination = {
    showSizeChanger: true,
    pageSizeOptions: [20, 40, 80, 100],
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
  };

  const columns = [
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => {
        return safeFormatDistance(text);
      }
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ...getColumnSearchProps('name')
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      ...getColumnSearchProps('email')
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      ...getColumnSearchProps('name')
    },
    {
      title: 'Verified',
      dataIndex: 'verified',
      key: 'verified',
      filters: [
        { text: 'True', value: true },
        { text: 'False', value: false }
      ],
      onFilter: (value, record) => record && record.verified === value,
      filterMultiple: false,
      render: (text, record, index) => {
        return String(text);
      }
    },
    {
      title: 'Onboarding',
      dataIndex: 'onboarding',
      key: 'onboarding',
      filters: [
        { text: 'True', value: true },
        { text: 'False', value: false }
      ],
      onFilter: (value, record) => record && record.onboarding === value,
      filterMultiple: false,
      render: (text, record, index) => {
        return String(text);
      }
    },
    {
      title: 'OTP',
      dataIndex: ['otp', 'enabled'],
      key: 'otp.enabled',
      filters: [
        { text: 'True', value: true },
        { text: 'False', value: false }
      ],
      onFilter: (value, record) => record && record.otp && record.otp.enabled === value,
      filterMultiple: false,
      render: (text, record, index) => {
        return String(text);
      }
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',

      filters: [
        { text: 'holder', value: 'holder' },
        { text: 'admin', value: 'admin' },
        { text: 'user', value: 'user' }
      ],
      sorter: (a, b) => a.role.localeCompare(b.role),
      onFilter: (value, record) => record && record.role.indexOf(value) === 0,
      filterMultiple: false
    },
    {
      title: 'Plan',
      dataIndex: ['billing', 'plan'],
      key: 'billing.plan',
      filters: BILLING_PLANS.map((plan) => ({ text: plan.name, value: plan.value })),
      onFilter: (value, record) => {
        const plan = record && record.billing && record.billing.plan;
        return plan === value;
      },
      render: (text, record, index) => {
        const planConfig = BILLING_PLANS.find((plan) => plan.value === (record && record.billing && record.billing.plan));

        const color = planConfig ? planConfig.color : undefined;
        const name = planConfig ? planConfig.name : String(text);

        return <span style={{ color: color }}>{name}</span>;
      }
    },
    {
      title: 'Plan Status',
      dataIndex: ['billing', 'planStatus'],
      key: 'billing.planStatus',
      filters: BILLING_PLANS_STATUSES.map((status) => ({ text: status.name, value: status.value })),
      onFilter: (value, record) => {
        const plan = record && record.billing && record.billing.planStatus;
        return plan === value;
      },
      render: (text, record, index) => {
        const planStatus = (record && record.billing && record.billing.planStatus) || null;

        const statusConfig = BILLING_PLANS_STATUSES.find((plan) => plan.value === planStatus);

        const color = statusConfig ? statusConfig.color : undefined;
        const name = statusConfig ? statusConfig.name : String(text);

        return <span style={{ color: color }}>{name}</span>;
      }
    },
    {
      title: 'LTD',
      dataIndex: ['lifeTime', 'enabled'],
      key: 'lifeTime.enabled',
      filters: [
        { text: 'True', value: true },
        { text: 'False', value: false }
      ],
      onFilter: (value, record) => record && record.lifeTime && record.lifeTime.enabled === value,
      filterMultiple: false,
      render: (value) => {
        return <span>{String(value)}</span>;
      }
    },
    {
      title: 'LTD Ver.',
      dataIndex: ['lifeTime', 'version'],
      key: 'lifeTime.version',
      filters: [
        { text: '1', value: 1 },
        { text: '2', value: 2 },
        { text: '3', value: 3 }
      ],
      onFilter: (value, record) => record && record.lifeTime && record.lifeTime.version === value,
      filterMultiple: false,
      render: (value) => {
        return <span>{String(value)}</span>;
      }
    },
    {
      title: 'Is LTD Paid',
      dataIndex: ['lifeTime', 'paid'],
      key: 'lifeTime.paid',
      filters: [
        { text: 'True', value: true },
        { text: 'False', value: false }
      ],
      onFilter: (value, record) => record && record.lifeTime && record.lifeTime.paid === value,
      filterMultiple: false,
      render: (value) => {
        return <span>{String(value)}</span>;
      }
    },
    {
      title: 'Trial End',
      dataIndex: ['billing', 'trialEnd'],
      key: 'billing.trialEnd',
      render: (value) => {
        return value ? safeFormatDistance(value) : dimmedNullJsx;
      }
    },
    {
      title: 'Unpaid Plan Timeout End',
      dataIndex: ['billing', 'unpaidPlanTimeoutEnd'],
      key: 'billing.unpaidPlanTimeoutEnd',
      render: (value) => {
        return value ? safeFormatDistance(value) : dimmedNullJsx;
      }
    },
    {
      title: 'Auth Type',
      dataIndex: 'authType',
      key: 'authType',
      filters: [
        { text: 'local', value: 'local' },
        { text: 'google', value: 'google' },
        { text: 'facebook', value: 'facebook' },
        { text: 'onetool', value: 'onetool' }
      ],
      sorter: (a, b) => a.authType.localeCompare(b.authType),
      onFilter: (value, record) => record && record.authType.indexOf(value) === 0,
      filterMultiple: false
    },
    {
      title: '€',
      dataIndex: 'subscription',
      key: 'subscription',
      sorter: (a, b) => a.subscription - b.subscription
    },
    {
      title: 'Owner',
      dataIndex: 'ownerEmail',
      key: 'ownerEmail',
      ...getColumnSearchProps('ownerEmail')
    },
    {
      title: 'Played Tutorials',
      dataIndex: ['_fake_index_'],
      key: 'playedTutorials',
      render: (text, record, index) => {
        const playedTutorials = _uniq(record.playedTutorials || []);
        const playedTutorialsCount = playedTutorials.length;

        if (!playedTutorialsCount) {
          return '---';
        }

        const sumCompletionRatio = _sumBy(playedTutorials, 'completionRatio') || 0;
        const sumCompletionPercentage = sumCompletionRatio * 100;
        const avgCompletionPercentage = sumCompletionPercentage / playedTutorialsCount;

        return (
          <span>
            <small style={{ opacity: 0.5 }}>Sum.</small> {round(sumCompletionPercentage)}%, <small style={{ opacity: 0.5 }}>Num.</small>{' '}
            {playedTutorialsCount}, <small style={{ opacity: 0.5 }}>Avg.</small> {round(avgCompletionPercentage)}%
          </span>
        );
      },
      sorter: (a, b) => {
        return getPlayedTutorialsSortNum(a.playedTutorials) - getPlayedTutorialsSortNum(b.playedTutorials);
      }
    },
    ...['forms', 'submissions', 'users', 'guests', 'filesStorage', 'domains'].map((key) => {
      return {
        title: `Usage.${key}`,
        dataIndex: ['planUsage', key],
        key: `planUsage.${key}`,
        sorter: (a, b) => Number(_get(a, `planUsage.${key}`)) - Number(_get(b, `planUsage.${key}`)) || 0
      };
    }),
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      align: 'right',
      render: (text, row, index) => (
        <>
          <Tooltip placement="top" title="User Details" arrowPointAtCenter>
            <Link to={`/users/${row._id}`}>
              <ProfileOutlined />
            </Link>
          </Tooltip>
        </>
      )
    }
  ];

  const DEFAULT_VISIBLE_COLUMNS_TITLES = ['Email', 'Role', 'Plan', 'Plan Status', 'LTD'];

  const allColumnsTitles = columns.filter((c) => c.title).map((c) => c.title);
  const [visibleColumnsTitles, setVisibleColumnsTitles] = useState(DEFAULT_VISIBLE_COLUMNS_TITLES);

  const onVisibleColumnsTitlesChange = (list) => {
    setVisibleColumnsTitles(list);
  };

  const filteredColumns = columns.filter((c) => !c.title || visibleColumnsTitles.includes(c.title));

  return (
    <>
      <PageHeader
        title="Users"
        style={{ border: '1px solid #f0f0f0', background: '#fff', margin: '0 0 15px 0' }}
        extra={[
          <Button type="primary" ghost key="mails" target="_blank" onClick={() => openMailsModal()}>
            Mails
          </Button>,
          <Button key="reload" onClick={() => reload()}>
            <ReloadOutlined />
          </Button>
        ]}
      />

      <Spin spinning={getUsersLoading} size="large">
        <Card style={{ width: '100%', minHeight: 224, padding: '40px 0 0 0' }}>
          <Checkbox.Group options={allColumnsTitles} value={visibleColumnsTitles} onChange={onVisibleColumnsTitlesChange} />

          <Divider />

          {users && !getUsersLoading && (
            <Table
              rowKey="_id"
              dataSource={users}
              columns={filteredColumns}
              pagination={pagination}
              onRow={(record) => {
                return {
                  onDoubleClick: () => history.push({ pathname: `/users/${record._id}` })
                };
              }}
            />
          )}
        </Card>
      </Spin>

      <Modal title="Email list" visible={showEmails} onOk={() => setShowEmails(false)} onCancel={() => setShowEmails(false)}>
        <Spin spinning={getMailsLoading} size="large">
          {mails && <Input.TextArea rows={5} onChange={() => {}} onFocus={(e) => e.target.select()} value={mails.join('\n')} />}
        </Spin>
      </Modal>
    </>
  );
};

export default withRouter(Users);
